import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';
import { forkJoin, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ShoppingCartService } from '../../../shared/services/apis/platform/shopping-cart.service';
import { KitPaymentService } from '../../../shared/services/apis/platform/kit-payment.service';
import { ShoppingCartKitItemRequest, ShoppingCartPostRequest } from '../../../shared/services/requests/platform/shopping-cart-post.request';
import { GetShoppingCartResponse } from '../../../shared/services/responses/platform/get-shopping-cart.response';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatFormField, MatLabel, MatError, MatFormFieldModule } from "@angular/material/form-field";
import { MatRadioGroup, MatRadioButton } from "@angular/material/radio";
import { KitPaymentBookComponent } from "./kit-payment-book/kit-payment-book.component";
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MenuComponent } from "../../../shared/components/menu/menu.component";
import { FooterComponent } from "../../../shared/components/footer/footer.component";
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MatButton } from '@angular/material/button';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { UserCreditCardRequest } from '../../../shared/services/requests/platform/book-payment.request';
import { PaymentMethodEnum } from '../../../shared/services/enums/payment-method.enum';
import { CorreiosEstimate } from '../../../shared/services/responses/platform/kit-details.response';
import { WaitingPaymentDialogKitComponent } from './waiting-payment-dialog-kit/waiting-payment-dialog-kit.component';
import { MatDialog } from '@angular/material/dialog';
import { ShoppingCartPostResponse } from '../../../shared/services/responses/platform/shopping-cart-post.response';
import { GetKitPaymentResponse } from '../../../shared/services/responses/platform/get-kit-payment.response';
import { BookPaymentStatusEnum } from '../../../shared/services/enums/book-payment-status.enum';
import { UtilService } from '../../../shared/services/util.service';
import { MatOption } from "@angular/material/core";
import { MatSelectModule } from '@angular/material/select';
import { PaymentMethodInstallment } from '../../../shared/services/models/platform/payment-method-installment.model';
import { cpfValidator } from '../../../shared/validators/cpf.validator';

@Component({
  selector: 'app-kit-payment',
  templateUrl: './kit-payment.component.html',
  styleUrls: ['./kit-payment.component.css'],
  imports: [MatProgressSpinner,
    MatFormField,
    MatLabel,
    MatError,
    MatRadioGroup,
    MatRadioButton,
    MatButton,
    FormsModule,
    ReactiveFormsModule,
    KitPaymentBookComponent,
    CommonModule,
    MatInputModule,
    MenuComponent,
    FooterComponent,
    NgxMaskDirective,
    MatFormFieldModule,
    MatSelectModule,
    MatOption],
  providers: [provideNgxMask()],
})
export class KitPaymentComponent implements OnInit, OnDestroy {

  addressForm: FormGroup;
  paymentForm: FormGroup;
  paymentPixForm: FormGroup;

  shoppingCartData: GetShoppingCartResponse;

  paymentMethod: 'creditCard' | 'pix' = 'creditCard';
  selectedShippingOption: string = '';
  authorDiscounts: { [idKit: number]: number } = {};
  authorDiscountsCode: { [idKit: number]: string } = {};

  private ip: string = '192.168.0.2';

  statusInterval: any = null;
  isLoading = true;
  private _shoppingCartPostResponse: ShoppingCartPostResponse | null = null;

  // Debounce das alterações de quantidade: acumula o último valor por kit e só envia 1s após o último clique
  private destroy$ = new Subject<void>();
  private pendingQuantities = new Map<number, number>();
  private quantityFlush$ = new Subject<void>();
  private flushInProgress = false;
  private cartRequestSeq = 0;
  hasPendingQuantityUpdate = false;
  isSearchingCep = false;
  lastValidatedCep: string | null = null;
  isCepValid = false;

  // Totais calculados
  subtotal: number = 0;
  totalShipping: number = 0;
  totalAuthorDiscount: number = 0;
  finalTotal: number = 0;
  juros: number = 0;

  estados = [
    { nome: 'Acre', sigla: 'AC' },
    { nome: 'Alagoas', sigla: 'AL' },
    { nome: 'Amapá', sigla: 'AP' },
    { nome: 'Amazonas', sigla: 'AM' },
    { nome: 'Bahia', sigla: 'BA' },
    { nome: 'Ceará', sigla: 'CE' },
    { nome: 'Distrito Federal', sigla: 'DF' },
    { nome: 'Espírito Santo', sigla: 'ES' },
    { nome: 'Goiás', sigla: 'GO' },
    { nome: 'Maranhão', sigla: 'MA' },
    { nome: 'Mato Grosso', sigla: 'MT' },
    { nome: 'Mato Grosso do Sul', sigla: 'MS' },
    { nome: 'Minas Gerais', sigla: 'MG' },
    { nome: 'Pará', sigla: 'PA' },
    { nome: 'Paraíba', sigla: 'PB' },
    { nome: 'Paraná', sigla: 'PR' },
    { nome: 'Pernambuco', sigla: 'PE' },
    { nome: 'Piauí', sigla: 'PI' },
    { nome: 'Rio de Janeiro', sigla: 'RJ' },
    { nome: 'Rio Grande do Norte', sigla: 'RN' },
    { nome: 'Rio Grande do Sul', sigla: 'RS' },
    { nome: 'Rondônia', sigla: 'RO' },
    { nome: 'Roraima', sigla: 'RR' },
    { nome: 'Santa Catarina', sigla: 'SC' },
    { nome: 'São Paulo', sigla: 'SP' },
    { nome: 'Sergipe', sigla: 'SE' },
    { nome: 'Tocantins', sigla: 'TO' }
  ];

  constructor(
    private fb: FormBuilder,
    private shoppingCartService: ShoppingCartService,
    private kitPaymentService: KitPaymentService,
    private router: Router,
    private http: HttpClient, // Para buscar CEP
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService,
  ) {
    this.addressForm = this.fb.group({
      deliveryZipcode: ['', [Validators.required, Validators.minLength(8)]],
      deliveryAddress: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(60)]],
      deliveryNumber: ['', [Validators.required, Validators.maxLength(10)]],
      deliveryNeighborhood: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(60)]],
      deliveryComplement: ['', [Validators.maxLength(60)]],
      deliveryCity: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(60)]],
      deliveryState: ['', Validators.required],
    });

    this.paymentForm = this.fb.group({
      cardName: ['', Validators.required],
      cardNumber: ['', [Validators.required]],
      expiryDate: ['', [Validators.required]],
      cpf: ['', [Validators.required, cpfValidator()]],
      cvc: ['', [Validators.required]],
      name: ['', [Validators.required]],
      email: ['', [Validators.required]],
      postalCode: ['', [Validators.required]],
      addressNumber: ['', [Validators.required]],
      addressComplement: ['', [Validators.required]],
      mobilePhone: ['', [Validators.required]],
      idPaymentMethodInstallment: ['', [Validators.required]],
    });

    this.paymentPixForm = this.fb.group({
      cpfPix: ['', [Validators.required, cpfValidator()]],
    });
  }

  ngOnInit(): void {
    this.loadShoppingCart();

    this.getIP();

    this.quantityFlush$
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(() => this.flushPendingQuantities());

    this.addressForm.get('deliveryZipcode')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        const cleanCep = (val || '').replace(/\D/g, '');
        if (cleanCep !== this.lastValidatedCep) {
          this.isCepValid = false;
        }
      });
  }

  ngOnDestroy() {
    clearInterval(this.statusInterval);

    // Best effort: envia alterações ainda pendentes antes de sair da tela
    this.pendingQuantities.forEach((quantity, idKit) =>
      this.shoppingCartService.updateQuantity(idKit, quantity).subscribe());
    this.pendingQuantities.clear();

    this.destroy$.next();
    this.destroy$.complete();
  }


  getInstallmentLabel(installment: PaymentMethodInstallment): string {
    let totalComJuros = this.calcularJuros(this.finalTotal, installment);
    let labelJuros: string = 'sem juros'
    if (totalComJuros != this.finalTotal)
      labelJuros = 'com juros'
    return `Em ${installment.installmentCount}x de R$ ${(totalComJuros / installment.installmentCount).toFixed(2)} - ${labelJuros}`;
  }

  changePaymentMethodInstallment() {
    const payment = this.paymentForm.value;

    let paymentMethodInstallment: PaymentMethodInstallment | undefined = this.shoppingCartData.listPaymentMethodInstallment.find(c => c.idPaymentMethodInstallment === payment.idPaymentMethodInstallment);

    if (!paymentMethodInstallment)
      return;

    let totalComJuros = this.calcularJuros(this.finalTotal, paymentMethodInstallment);
    this.juros = totalComJuros - this.finalTotal;
  }

  calcularJuros(value: number, paymentMethodInstallment: PaymentMethodInstallment) {
    const count = paymentMethodInstallment.installmentCount;
    const interest = paymentMethodInstallment.installmentInterest;
    let parcela = 0;
    let totalComJuros = 0;

    if (interest > 0 && count > 1) {
      // Fórmula Price (juros compostos)
      const i = interest / 100;
      const n = count;
      parcela = (value * i) / (1 - Math.pow(1 + i, -n));
      totalComJuros = parcela * n;
    } else {
      parcela = value / count;
      totalComJuros = parcela * count;
    }

    return totalComJuros;
  }

  getIP(): void {
    this.utilService.getIP().subscribe({
      next: response => {
        if (response && response.ip) {
          this.ip = response.ip;
        }
      },
    });
  }

  loadShoppingCart(onComplete?: () => void): void {

    const requestSeq = ++this.cartRequestSeq;
    const address = this.addressForm.value;
    let zipcode = address.deliveryZipcode.replace(/\D/g, '');

    this.shoppingCartService.getShoppingCart(zipcode)
      .pipe(finalize(() => {
        this.isLoading = false;
        if (requestSeq === this.cartRequestSeq)
          onComplete?.();
      }))
      .subscribe((response: GetShoppingCartResponse) => {
        // Descarta respostas fora de ordem (um reload mais novo já foi disparado)
        if (requestSeq !== this.cartRequestSeq)
          return;

        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription || 'Não foi possível carregar o carrinho. Tente novamente.', AlertType.error);
          return;
        }

        if (response.listShoppingCart.length === 0) {
          this.alertService.show('Erro', "Não há itens no carrinho para compra", AlertType.error);
          this.router.navigate(['/']);
        }

        this.shoppingCartData = response;

        // Se a opção de frete escolhida ficou indisponível (ex.: peso excedeu o limite), limpa a seleção
        if (this.selectedShippingOption) {
          const selected = response.listCorreios?.find(c => c.serviceCode === this.selectedShippingOption);
          if (!selected || selected.isDisabledDueToWeight) {
            this.selectedShippingOption = '';
            this.alertService.show('Atenção', 'A opção de frete selecionada ficou indisponível pelo peso do pedido. Escolha outra modalidade.', AlertType.warning);
          }
        }

        //Calcula lista corretios
        this.calculateTotals();
      }, error => {
        console.error('Erro ao buscar o carrinho', error);
      });
  }

  onCepChange(event?: any): void {
    const rawValue = event?.target?.value !== undefined ? event.target.value : this.addressForm.get('deliveryZipcode')?.value;
    const cep = (rawValue || '').replace(/\D/g, '');

    if (!cep) {
      this.isCepValid = false;
      this.lastValidatedCep = null;
      return;
    }

    if (cep.length !== 8) {
      this.isCepValid = false;
      this.lastValidatedCep = null;
      this.addressForm.get('deliveryZipcode')?.setErrors({ minlength: true });
      this.addressForm.get('deliveryZipcode')?.markAsTouched();
      return;
    }

    if (this.isCepValid && this.lastValidatedCep === cep) {
      return;
    }

    this.isSearchingCep = true;
    this.http.get(`https://viacep.com.br/ws/${cep}/json/`)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSearchingCep = false;
        })
      )
      .subscribe({
        next: (data: any) => {
          if (!data || data.erro) {
            this.isCepValid = false;
            this.lastValidatedCep = null;
            this.addressForm.get('deliveryZipcode')?.setErrors({ cepInvalido: true });
            this.addressForm.get('deliveryZipcode')?.markAsTouched();
            this.addressForm.patchValue({
              deliveryAddress: '',
              deliveryNeighborhood: '',
              deliveryCity: '',
              deliveryState: ''
            });
            this.selectedShippingOption = '';
            if (this.shoppingCartData) {
              this.shoppingCartData.listCorreios = [];
            }
            this.calculateTotals();
            this.alertService.show('Atenção', 'CEP não encontrado nos Correios. Por favor, verifique o CEP informado.', AlertType.warning);
          } else {
            this.isCepValid = true;
            this.lastValidatedCep = cep;
            this.addressForm.get('deliveryZipcode')?.setErrors(null);
            this.addressForm.patchValue({
              deliveryAddress: (data.logradouro || '').substring(0, 60),
              deliveryNeighborhood: (data.bairro || '').substring(0, 60),
              deliveryCity: (data.localidade || '').substring(0, 60),
              deliveryState: data.uf || ''
            });
            this.loadShoppingCart();
          }
        },
        error: (err) => {
          console.error('Erro ao buscar CEP', err);
          this.isCepValid = false;
          this.lastValidatedCep = null;
          this.addressForm.get('deliveryZipcode')?.setErrors({ cepInvalido: true });
          this.addressForm.get('deliveryZipcode')?.markAsTouched();
          this.addressForm.patchValue({
            deliveryAddress: '',
            deliveryNeighborhood: '',
            deliveryCity: '',
            deliveryState: ''
          });
          this.selectedShippingOption = '';
          if (this.shoppingCartData) {
            this.shoppingCartData.listCorreios = [];
          }
          this.calculateTotals();
          this.alertService.show('Erro', 'Não foi possível consultar o CEP nos Correios. Tente novamente.', AlertType.error);
        }
      });
  }

  onQuantityChange(event: { idKit: number, quantity: number }): void {
    const item = this.shoppingCartData.listShoppingCart.find(i => i.idKit === event.idKit);
    if (!item)
      return;

    item.quantity = event.quantity; // UI otimista; o request sai 1s após o último clique
    this.pendingQuantities.set(event.idKit, event.quantity);
    this.hasPendingQuantityUpdate = true;
    this.quantityFlush$.next();
  }

  private flushPendingQuantities(): void {
    if (this.flushInProgress || this.pendingQuantities.size === 0)
      return;

    this.flushInProgress = true;
    const updates = Array.from(this.pendingQuantities.entries());
    this.pendingQuantities.clear();

    forkJoin(updates.map(([idKit, quantity]) => this.shoppingCartService.updateQuantity(idKit, quantity)))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.flushInProgress = false;

          // Novos cliques chegaram durante o envio: manda antes de recarregar
          if (this.pendingQuantities.size > 0) {
            this.flushPendingQuantities();
            return;
          }

          this.loadShoppingCart(() => {
            this.hasPendingQuantityUpdate = this.pendingQuantities.size > 0 || this.flushInProgress;
          });
        },
        error: () => {
          this.flushInProgress = false;
          this.hasPendingQuantityUpdate = false;
          this.alertService.show('Erro', 'Não foi possível atualizar a quantidade. Tente novamente.', AlertType.error);
          this.loadShoppingCart();
        }
      });
  }

  onApplyDiscount(event: { idKit: number, discountCode: string, discountValue: number }): void {
    this.authorDiscounts[event.idKit] = event.discountValue;
    this.authorDiscountsCode[event.idKit] = event.discountCode;
    this.calculateTotals();
  }

  get allShippingOptionsBlocked(): boolean {
    const needsShipping = this.shoppingCartData?.listShoppingCart?.some(item => item.includesBook);
    const options = this.shoppingCartData?.listCorreios;
    return !!needsShipping && !!options?.length && options.every(option => option.isDisabledDueToWeight);
  }

  get totalCartWeightKg(): number {
    return this.shoppingCartData?.listCorreios?.[0]?.weightKg ?? 0;
  }

  calculateTotals(): void {
    this.subtotal = this.shoppingCartData.sunTotalPriceKits;

    // Frete é calculado pelo backend, mas podemos somar aqui se necessário.
    // Usaremos o valor total do serviço.
    this.totalShipping = this.selectedShippingOption
      ? this.shoppingCartData.listCorreios?.find(c => c.serviceCode == this.selectedShippingOption)?.price || 0
      : 0;


    this.totalAuthorDiscount = Object.values(this.authorDiscounts).reduce((acc, value) => acc + value, 0);

    this.finalTotal = this.subtotal - this.totalAuthorDiscount + this.totalShipping;
  }

  onPaymentMethodChange(method: 'creditCard' | 'pix'): void {
    this.paymentMethod = method;
    if (method === 'pix') {
      // Limpa as validações do formulário de cartão
      Object.keys(this.paymentForm.controls).forEach(key => {
        this.paymentForm.get(key)?.clearValidators();
        this.paymentForm.get(key)?.updateValueAndValidity();
      });
      this.juros = 0;
      this.paymentForm.get('idPaymentMethodInstallment')?.setValue('');
    } else {
      // Adiciona as validações de volta
      Object.keys(this.paymentForm.controls).forEach(key => {
        this.paymentForm.get(key)?.setValidators([Validators.required]); // Adicione validadores específicos se necessário
        this.paymentForm.get(key)?.updateValueAndValidity();
      });
    }
  }

  onSubmit(): void {
    if (this.hasPendingQuantityUpdate) {
      this.alertService.show('Atenção', 'Aguarde a atualização dos valores do carrinho para finalizar a compra.', AlertType.warning);
      return;
    }

    if (this.isSearchingCep) {
      this.alertService.show('Atenção', 'Aguarde a validação do CEP junto aos Correios.', AlertType.warning);
      return;
    }

    const currentZip = (this.addressForm.get('deliveryZipcode')?.value || '').replace(/\D/g, '');
    if (!this.isCepValid || this.lastValidatedCep !== currentZip || currentZip.length !== 8) {
      this.addressForm.get('deliveryZipcode')?.setErrors({ cepInvalido: true });
      this.addressForm.get('deliveryZipcode')?.markAsTouched();
      this.alertService.show('Atenção', 'Informe um CEP válido com retorno dos Correios para finalizar a compra.', AlertType.warning);
      return;
    }

    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      alert('Por favor, preencha todos os campos de endereço.');
      return;
    }
    if (this.paymentMethod === 'creditCard' && this.paymentForm.invalid) {
      this.paymentForm.markAllAsTouched();
      alert('Por favor, preencha todos os campos de pagamento.');
      return;
    }

    if (this.paymentMethod === 'pix' && this.paymentPixForm.invalid) {
      this.paymentPixForm.markAllAsTouched();
      alert('Por favor, preencha todos para realização do PIX.');
      return;
    }

    if (this.allShippingOptionsBlocked) {
      this.alertService.show('Atenção', 'O peso total do pedido excede o limite das modalidades de entrega. Reduza a quantidade de livros ou divida a compra em pedidos separados.', AlertType.warning);
      return;
    }

    if (this.shoppingCartData.listShoppingCart.some(item => item.includesBook) && !this.selectedShippingOption) {
      alert('Por favor, selecione uma opção de frete.');
      return;
    }

    const selectedOption = this.shoppingCartData.listCorreios?.find(c => c.serviceCode === this.selectedShippingOption);
    if (selectedOption?.isDisabledDueToWeight) {
      this.alertService.show('Atenção', 'A opção de frete selecionada não suporta o peso do pedido. Escolha outra modalidade.', AlertType.warning);
      return;
    }

    this.isLoading = true;
    const request = this.buildPaymentRequest();

    this.kitPaymentService.post(request)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe((response: ShoppingCartPostResponse) => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription || response.message || 'Não foi possível concluir o pagamento. Tente novamente.', AlertType.error);
          return;
        }

        this._shoppingCartPostResponse = response;

        if (this.paymentMethod === 'pix' && response.pixData) {
          this.openWaitingPaymentModal(response.pixData.pixCopyPaste, response.pixData.pixQrCode)
          this.monitorPaymentStatus();
        } else {
          // Redirecionar para página de sucesso
          this.openWaitingPaymentModal()
          this.monitorPaymentStatus();
        }

      }, error => {
        console.error('Erro na requisição de pagamento', error);
        this.alertService.show('Erro', 'Ocorreu um erro inesperado. Tente novamente.', AlertType.error);
      });
  }

  private buildPaymentRequest(): ShoppingCartPostRequest {
    const request = new ShoppingCartPostRequest();

    // Endereço
    const address = this.addressForm.value;
    const paymentPixForm = this.paymentPixForm.value;

    request.deliveryZipcode = address.deliveryZipcode.replace(/\D/g, '');
    request.deliveryAddress = address.deliveryAddress;
    request.deliveryNumber = address.deliveryNumber;
    request.deliveryNeighborhood = address.deliveryNeighborhood;
    request.deliveryComplement = address.deliveryComplement;
    request.deliveryCity = address.deliveryCity;
    request.deliveryState = address.deliveryState;
    request.cpfPix = paymentPixForm.cpfPix;
    request.finalPrice = this.finalTotal + this.juros;

    if (this.shoppingCartData.listShoppingCart.some(item => item.includesBook)) {
      request.deliveryType = this.selectedShippingOption;
      request.deliveryPrice = this.shoppingCartData.listCorreios?.find(c => c.serviceCode == request.deliveryType)?.price;
    }

    if (this.paymentMethod === 'creditCard') {
      const payment = this.paymentForm.value;
      request.idPaymentMethod = PaymentMethodEnum.CartaoParcelado;
      request.userCreditCard = new UserCreditCardRequest();
      request.userCreditCard.holderName = payment.cardName;
      request.userCreditCard.number = payment.cardNumber;
      request.userCreditCard.cpfCnpj = payment.cpf;
      request.userCreditCard.ccv = payment.cvc;
      request.userCreditCard.expiryMonth = payment.expiryDate[0] + payment.expiryDate[1]
      request.userCreditCard.expiryYear = payment.expiryDate[2] + payment.expiryDate[3]
      request.userCreditCard.name = payment.name;
      request.userCreditCard.email = payment.email;
      request.userCreditCard.postalCode = payment.postalCode;
      request.userCreditCard.addressNumber = payment.addressNumber;
      request.userCreditCard.addressComplement = payment.addressComplement;
      request.userCreditCard.mobilePhone = payment.mobilePhone;
      request.userCreditCard.phone = payment.mobilePhone;
      request.userCreditCard.remoteIp = this.ip;
      request.idPaymentMethodInstallment = payment.idPaymentMethodInstallment
    } else {
      request.idPaymentMethod = PaymentMethodEnum.PIX;
    }

    // Frete e Kits
    // A lógica de qual frete foi escolhido precisa ser implementada
    // Assumindo que o `selectedShippingOption` guarda o código do serviço (PAC, SEDEX)

    //req.shippingService = this.selectedShippingOption;

    // Você precisará mapear os kits e seus descontos para a requisição
    // A estrutura da `ShoppingCartPostRequest` precisa ser definida para isso.
    request.kits = []
    this.shoppingCartData.listShoppingCart.forEach(element => {
      let shoppingCartKitItemRequest: ShoppingCartKitItemRequest = new ShoppingCartKitItemRequest();

      shoppingCartKitItemRequest.idKit = element.idKit;
      shoppingCartKitItemRequest.quantity = element.quantity;
      const totalDiscount = this.authorDiscounts[shoppingCartKitItemRequest.idKit] ?? 0;
      const discountPerUnit = element.quantity > 0 ? totalDiscount / element.quantity : 0;
      shoppingCartKitItemRequest.priceUnit = element.totalPrice - discountPerUnit;
      shoppingCartKitItemRequest.discountAuthorCode = this.authorDiscountsCode[shoppingCartKitItemRequest.idKit]

      if (element.listCorreios) {
        shoppingCartKitItemRequest.priceDeliveryTotal = element.listCorreios.find(c => c.serviceCode == request.deliveryType)?.price;
      }


      request.kits.push(shoppingCartKitItemRequest);
    });

    return request;
  }

  changeShipping() {
    this.calculateTotals();
  }
  isWaitingPayment = false;
  openWaitingPaymentModal(pixCopyPaste?: string | null | undefined, pixQRCode?: string | null | undefined): void {
    this.isWaitingPayment = true;
    this.dialog.open(WaitingPaymentDialogKitComponent, {
      disableClose: true,
      data: {
        title: 'Aguardando confirmaçao de pagamento',
        message: 'Assim que for confirmado o pagamento, você poderá continuar!',
        pixCopyPaste: pixCopyPaste,
        pixQRCode: pixQRCode
      }
    });
  }

  monitorPaymentStatus() {
    this.statusInterval = setInterval(() => {
      this.verifyPayment();
    }, 10000); // 10000 milissegundos = 10 segundos
  };

  verifyPayment() {
    if (this._shoppingCartPostResponse == null)
      return;

    this.kitPaymentService.get(this._shoppingCartPostResponse.idKitPayment)
      .subscribe((getKitPaymentResponse: GetKitPaymentResponse) => {
        if (getKitPaymentResponse.kitPayment.idPaymentStatus == BookPaymentStatusEnum.Aprovado) {
          clearInterval(this.statusInterval);
          this.closeWaitingPaymentModal();
          this.router.navigate([`/sucesso-pagamento`], { queryParams: { idKitPayment: getKitPaymentResponse.kitPayment.idKitPayment } });
        }
      }, error => {
        console.error('Erro na requisição de pagamento', error);
        this.alertService.show('Erro', 'Ocorreu um erro inesperado. Tente novamente.', AlertType.error);
      });
  }

  closeWaitingPaymentModal(): void {
    this.isWaitingPayment = false;
    this.dialog.closeAll();
  }

}